import { useAppTranslation } from "@/utils";
import { Badge, Button, Drawer, Empty, Flex, Grid, List, Tabs, Tag, Typography } from "@payping/web-components";
import { Close, CuToman } from "@payping/icons";
import { moment } from "@payping/utils/dateUtils";
import { ComponentProps, FC, useState } from "react";
import { MerchantInstallmentStatus } from "@payping/api/app/api/bnplMerchantTypes";
import { BnplCalenderDataCell } from "./index";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { selectAtom } from "jotai/utils";
import { useAtom } from "jotai";
import { dashboardPreferencesAtom } from "@/core/state";
import { LANGUAGES } from "@payping/utilities-shared/consts";

const languageAtom = selectAtom(dashboardPreferencesAtom, (val) => val.language);

interface DrawerProps {
  data?: BnplCalenderDataCell;
  date?: moment.Moment;
  open: boolean;
  onClose: () => void;
}
const BnplFinancialCalenderPageCalenderDrawer: FC<DrawerProps> = ({ onClose, open, data, date }) => {
  const { t } = useAppTranslation();
  const [language] = useAtom(languageAtom);

  const havePaid = data ? data[MerchantInstallmentStatus.Paid]?.list.length : false;
  const haveDue = data ? data[MerchantInstallmentStatus.Due].list.length : false;
  const haveDelayed = data ? data[MerchantInstallmentStatus.Delayed].list.length : false;
  const { sm } = Grid.useBreakpoint();

  const [activeKey, setActiveKey] = useState<string>(
    String(
      (havePaid && MerchantInstallmentStatus.Paid) ||
        (haveDue && MerchantInstallmentStatus.Due) ||
        (haveDelayed && MerchantInstallmentStatus.Delayed),
    ),
  );

  const ItemList = (() => {
    const temp: ComponentProps<typeof Tabs>["items"] = [];
    if (havePaid) {
      temp.push({
        label: t("shop.bnpl.installmentStatus.completed"),
        key: MerchantInstallmentStatus.Paid.toString(),
      });
    }
    if (haveDue) {
      temp.push({
        label: t("common.ahead"),
        key: MerchantInstallmentStatus.Due.toString(),
      });
    }
    if (haveDelayed) {
      temp.push({
        label: t("shop.bnpl.installmentStatus.delayed"),
        key: MerchantInstallmentStatus.Delayed.toString(),
      });
    }

    return temp;
  })();

  return (
    <Drawer
      onClose={onClose}
      open={open}
      placement={sm ? "left" : "bottom"}
      height={"100%"}
      title={
        language === LANGUAGES.ENGLISH
          ? moment(date).locale("en").format("dddd, DD MMMM YYYY")
          : moment(date).locale("fa").format("dddd, jD jMMMM jYYYY")
      }
      closeIcon={false}
      zIndex={2005}
      extra={
        <Button
          type={"text"}
          icon={<Close width={14} height={14} />}
          onClick={() => {
            onClose();
          }}
        />
      }
    >
      <Flex vertical gap={"middle"} className="tw-w-full">
        {data ? (
          <>
            <Flex vertical gap={"small"} className="tw-w-full">
              {havePaid ? (
                <Flex className="tw-w-full" align="center" justify="space-between">
                  <Flex gap={"small"} align="center">
                    <Badge color="green" />
                    <Typography.Text>{data[MerchantInstallmentStatus.Paid].number}</Typography.Text>
                    <Typography.Text type="secondary">{`${t("common.installment")} ${t("shop.bnpl.installmentStatus.completed")}`}</Typography.Text>
                  </Flex>
                  <Tag color="green">
                    <Flex align="center">
                      <Typography.Text>
                        {formatLocalizedNumber({ value: data[MerchantInstallmentStatus.Paid].totalAmount })}
                      </Typography.Text>
                      <CuToman width={24} height={24} />
                    </Flex>
                  </Tag>
                </Flex>
              ) : null}
              {haveDue ? (
                <Flex className="tw-w-full" align="center" justify="space-between">
                  <Flex gap={"small"} align="center">
                    <Badge color="blue" />
                    <Typography.Text>{data[MerchantInstallmentStatus.Due].number}</Typography.Text>
                    <Typography.Text type="secondary">{`${t("common.installment")} ${t("common.ahead")}`}</Typography.Text>
                  </Flex>
                  <Tag color="blue">
                    <Flex align="center">
                      <Typography.Text>
                        {formatLocalizedNumber({ value: data[MerchantInstallmentStatus.Due].totalAmount })}
                      </Typography.Text>
                      <CuToman width={24} height={24} />
                    </Flex>
                  </Tag>
                </Flex>
              ) : null}
              {haveDelayed ? (
                <Flex className="tw-w-full" align="center" justify="space-between">
                  <Flex gap={"small"} align="center">
                    <Badge color="red" />
                    <Typography.Text>{data[MerchantInstallmentStatus.Delayed].number}</Typography.Text>
                    <Typography.Text type="secondary">{`${t("common.installment")} ${t("shop.bnpl.installmentStatus.delayed")}`}</Typography.Text>
                  </Flex>
                  <Tag color="red">
                    <Flex align="center">
                      <Typography.Text>
                        {formatLocalizedNumber({ value: data[MerchantInstallmentStatus.Delayed].totalAmount })}
                      </Typography.Text>
                      <CuToman width={24} height={24} />
                    </Flex>
                  </Tag>
                </Flex>
              ) : null}
            </Flex>
            <Tabs onChange={(key) => setActiveKey(key)} style={{ border: 0 }} items={ItemList} activeKey={activeKey} />
            <Flex vertical className="tw-w-full">
              {activeKey === MerchantInstallmentStatus.Due.toString() ? (
                <List
                  size="large"
                  className="!tw-border-color-border-secondary"
                  bordered
                  dataSource={data[MerchantInstallmentStatus.Due].list}
                  itemLayout="vertical"
                  renderItem={(item) => (
                    <List.Item>
                      <Flex align="center" justify="space-between">
                        <Typography.Text>{item.consumerName ?? ""}</Typography.Text>
                        <Flex align="center">
                          <Typography.Title className="!tw-m-0" level={5}>
                            <strong>{formatLocalizedNumber({ value: item.finalAmount ?? 0 })}</strong>
                          </Typography.Title>
                          <CuToman width={24} height={24} />
                        </Flex>
                      </Flex>
                    </List.Item>
                  )}
                />
              ) : null}
              {activeKey === MerchantInstallmentStatus.Paid.toString() ? (
                <List
                  size="large"
                  className="!tw-border-color-border-secondary"
                  bordered
                  dataSource={data[MerchantInstallmentStatus.Paid].list}
                  itemLayout="vertical"
                  renderItem={(item) => (
                    <List.Item>
                      <Flex align="center" justify="space-between">
                        <Typography.Text>{item.consumerName ?? ""}</Typography.Text>
                        <Flex align="center">
                          <Typography.Title className="!tw-m-0" level={5}>
                            <strong>{formatLocalizedNumber({ value: item.finalAmount ?? 0 })}</strong>
                          </Typography.Title>
                          <CuToman width={24} height={24} />
                        </Flex>
                      </Flex>
                    </List.Item>
                  )}
                />
              ) : null}
              {activeKey === MerchantInstallmentStatus.Delayed.toString() ? (
                <List
                  size="large"
                  className="!tw-border-color-border-secondary"
                  bordered
                  dataSource={data[MerchantInstallmentStatus.Delayed].list}
                  itemLayout="vertical"
                  renderItem={(item) => (
                    <List.Item>
                      <Flex align="center" justify="space-between">
                        <Typography.Text>{item.consumerName ?? ""}</Typography.Text>
                        <Flex align="center">
                          <Typography.Title className="!tw-m-0" level={5}>
                            <strong>{formatLocalizedNumber({ value: item.finalAmount ?? 0 })}</strong>
                          </Typography.Title>
                          <CuToman width={24} height={24} />
                        </Flex>
                      </Flex>
                    </List.Item>
                  )}
                />
              ) : null}
            </Flex>
          </>
        ) : (
          <div className="tw-pt-24">
            <Empty description={t("shop.bnpl.noInstallmentFoundInDate")} />
          </div>
        )}
      </Flex>
    </Drawer>
  );
};

export default BnplFinancialCalenderPageCalenderDrawer;
